import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  MeasuringStrategy,
  type Modifier,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import {
  restrictToHorizontalAxis,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  SortingStrategy,
  defaultAnimateLayoutChanges,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type FC, type ReactNode, memo, useMemo, useState } from "react";
import type { Project } from "senf-shared/types";
import { cn } from "../../../util/classNameConstructor";

function reorder<T>(list: T[], startIndex: number, endIndex: number): T[] {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

export function useOnDragEnd<
  T extends { uid?: string; name?: string; value?: string },
>(items: T[], callback: (items: T[]) => void) {
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id === over?.id) return;

    const getItemId = (item: T) => item.uid || item.name || item.value;

    const sorted = reorder(
      items,
      items.findIndex((item) => getItemId(item) === active.id),
      items.findIndex((item) => getItemId(item) === over?.id),
    );
    callback(sorted);
  };
  return onDragEnd;
}

interface SectionWrapperProps {
  listeners?: SyntheticListenerMap;
  className?: string;
  Component?: React.FC<any>;
  componentProps?: any;
  children?: React.ReactNode;
}

export function SortableWrapper({
  className,
  Component,
  listeners,
  componentProps,
  children,
}: SectionWrapperProps) {
  return (
    <div className={cn(className)}>
      {children}
      {Component && (
        <Component listeners={listeners} {...(componentProps || {})} />
      )}
    </div>
  );
}

export function SectionWrapper({
  className,
  children,
  ...props
}: SectionWrapperProps) {
  return (
    <SortableWrapper
      className={cn(
        "bg-surface-default col-span-full rounded-2xl overflow-hidden",
        className,
      )}
      {...props}
    >
      {children}
    </SortableWrapper>
  );
}

const SortableSection = ({
  Component,
  componentProps,
  id,
  Wrapper = SortableWrapper,
  ...props
}) => {
  const {
    attributes,
    listeners,
    isDragging,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id,
    animateLayoutChanges: (args) => {
      return defaultAnimateLayoutChanges({ ...args, wasDragging: true });
    },
  });
  const style = {
    opacity: isDragging && id ? 0 : 1,
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div
      style={style}
      ref={setNodeRef}
      {...attributes}
      {...(Component ? {} : listeners)}
      className={cn("group/section relative")}
      role="listitem"
    >
      <Wrapper
        {...props}
        listeners={listeners}
        Component={Component}
        componentProps={componentProps}
      />
    </div>
  );
};

interface SortableSectionsProps<
  T = { uid?: string; name?: string; value?: string; [key: string]: any },
  C = any,
  W = any,
> {
  items: T[];
  setItems?: React.Dispatch<React.SetStateAction<T[]>>;
  global: any;
  className?: string;
  Component: FC<C>;
  Wrapper?: FC<W>;
  axis?: "x" | "y";
  disabled?: boolean;
  divider?: ReactNode;
}

export const SortableSections = ({
  items,
  setItems,
  Component,
  global,
  className,
  Wrapper,
  axis,
  disabled,
  divider,
}: SortableSectionsProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const handleDragStart = ({ active }) => setActiveId(active.id as string);
  const handleDragCancel = () => setActiveId(null);
  const handleDragEnd = useOnDragEnd(items, (sorted) => {
    setActiveId(null);
    setItems?.(sorted);
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
  );

  const getItemId = (item: T) => item.uid || item.name || item.value;

  const activeIndex = useMemo(
    () => items?.findIndex((item) => getItemId(item) === activeId),
    [activeId, items],
  );
  const activeItem = useMemo(() => items[activeIndex], [activeIndex, items]);

  if (!items?.length) return null;

  const modifiers: Modifier[] = [];
  let strategy = rectSortingStrategy;
  if (axis === "y") {
    modifiers.push(restrictToVerticalAxis);
    strategy = verticalListSortingStrategy;
  }
  if (axis === "x") {
    modifiers.push(restrictToHorizontalAxis);
    strategy = horizontalListSortingStrategy;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      modifiers={modifiers}
    >
      <SortableContext
        items={items.map(getItemId)}
        strategy={strategy}
        disabled={disabled}
      >
        <div className={className}>
          {items.map((item, index) => {
            return (
              <div key={getItemId(item)}>
                {index === 0 && typeof divider === "function"
                  ? divider(0)
                  : index > 0 && divider && typeof divider === "function"
                    ? divider(index)
                    : divider}
                <SortableSection
                  key={getItemId(item)}
                  id={getItemId(item)}
                  Wrapper={Wrapper}
                  Component={Component}
                  componentProps={{
                    item,
                    global,
                    index,
                  }}
                />
              </div>
            );
          })}
          {/* {items.length > 0 && divider && typeof divider === 'function' ? divider(items.length) : divider} */}
        </div>
      </SortableContext>

      <DragOverlay>
        <SortableSection
          id={activeId}
          Wrapper={Wrapper}
          Component={Component}
          componentProps={{
            item: activeItem,
            index: activeIndex,
            global,
            dragOverlay: true,
          }}
        />
      </DragOverlay>
    </DndContext>
  );
};
